import React, { Component, ErrorInfo, ReactNode } from 'react';

export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  maxRetries?: number;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  enableRetry?: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private readonly retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call external error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    const { maxRetries = 3 } = this.props;

    if (this.state.retryCount < maxRetries) {
      this.setState((prevState) => ({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: prevState.retryCount + 1,
      }));
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { enableRetry = true, maxRetries = 3 } = this.props;
    const { hasError, error, errorInfo, retryCount } = this.state;

    if (hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const canRetry = enableRetry && retryCount < maxRetries;

      return (
        <div className="alert alert-danger" role="alert">
          <h4>Something went wrong</h4>
          <p>We're sorry, but there was an error loading the blog content.</p>

          {retryCount > 0 && (
            <p className="text-warning">
              <small>
                Retry attempt {retryCount} of {maxRetries}
              </small>
            </p>
          )}

          {process.env.NODE_ENV === 'development' && error && (
            <details style={{ whiteSpace: 'pre-wrap', marginTop: '1rem' }}>
              <summary>Error Details (Development Only)</summary>
              <strong>Error:</strong> {error.message}
              <br />
              <strong>Stack:</strong> {error.stack}
              {errorInfo && (
                <>
                  <br />
                  <strong>Component Stack:</strong> {errorInfo.componentStack}
                </>
              )}
            </details>
          )}

          <div className="mt-3">
            {canRetry && (
              <button className="btn btn-warning me-2" onClick={this.handleRetry}>
                Try Again ({maxRetries - retryCount} attempts left)
              </button>
            )}
            <button className="btn btn-primary" onClick={this.handleReload}>
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

// Error Display Component for non-boundary errors
export const ErrorDisplay: React.FC<{
  error: { message: string; type?: string };
  onRetry?: () => void;
  onDismiss?: () => void;
}> = ({ error, onRetry, onDismiss }) => {
  return (
    <div className="alert alert-warning alert-dismissible" role="alert">
      <strong>Error:</strong> {error.message}
      {error.type && <small className="d-block text-muted">Type: {error.type}</small>}
      <div className="mt-2">
        {onRetry && (
          <button className="btn btn-sm btn-warning me-2" onClick={onRetry}>
            Retry
          </button>
        )}
        {onDismiss && (
          <button className="btn btn-sm btn-secondary" onClick={onDismiss}>
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

// Network Error Component
export const NetworkErrorDisplay: React.FC<{
  onRetry?: () => void;
}> = ({ onRetry }) => {
  return (
    <div className="alert alert-warning" role="alert">
      <h6>Network Error</h6>
      <p>Unable to connect to the server. Please check your internet connection.</p>
      {onRetry && (
        <button className="btn btn-sm btn-warning" onClick={onRetry}>
          Try Again
        </button>
      )}
    </div>
  );
};
